import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL, STORAGE_KEYS } from '../../utils/constants';

type RetriableRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
};

export class ApiError extends Error {
  readonly status?: number;
  readonly code?: string;
  readonly retryAfterSeconds?: number;
  readonly originalError: unknown;

  constructor(
    message: string,
    status: number | undefined,
    originalError: unknown,
    code?: string,
    retryAfterSeconds?: number,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.retryAfterSeconds = retryAfterSeconds;
    this.originalError = originalError;
  }
}

export const isApiErrorStatus = (error: unknown, status: number): boolean => {
  if (error instanceof ApiError) return error.status === status;
  return axios.isAxiosError(error) && error.response?.status === status;
};

export const createApiHttpClient = (axiosInstance?: AxiosInstance): AxiosInstance => {
  const instance = axiosInstance ?? axios.create({
    baseURL: API_BASE_URL,
    // Both access and refresh tokens are HttpOnly cookies. Credentials must
    // therefore be sent for every API call.
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  instance.interceptors.request.use(
    (config) => {
      return config;
    },
    (error) => Promise.reject(error)
  );

  let refreshPromise: Promise<void> | null = null;

  const clearLocalSession = () => {
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);
  };

  // In cookie-auth mode the browser may legitimately call /auth/me before a
  // user has signed in. Only attempt token recovery for a session that the
  // frontend already knows about; otherwise an anonymous 401 would redirect
  // the login page to itself indefinitely.
  const hasLocalSession = (): boolean => Boolean(localStorage.getItem(STORAGE_KEYS.USER));

  const refreshAccessToken = async (): Promise<void> => {
    if (!refreshPromise) {
      refreshPromise = instance.post('/auth/refresh')
        .then(() => undefined)
        .finally(() => {
          refreshPromise = null;
        });
    }
    await refreshPromise;
  };

  const isAuthRequest = (url: string) => (
    url.includes('/auth/login')
    || url.includes('/auth/register')
    || url.includes('/auth/refresh')
    || url.includes('/auth/logout')
  );

  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<unknown>) => {
      const requestConfig = error.config as RetriableRequestConfig | undefined;
      const requestUrl = requestConfig?.url || '';

      if (error.response?.status === 401 && !isAuthRequest(requestUrl) && hasLocalSession()) {
        if (!requestConfig?._retry) {
          if (requestConfig) {
            requestConfig._retry = true;
          }
          try {
            await refreshAccessToken();
            if (requestConfig) {
              return instance.request(requestConfig);
            }
          } catch {
            // The original 401 is normalized below after session cleanup.
          }
        }

        clearLocalSession();
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
      }

      if (error.response?.data) {
        const errorData = error.response.data;
        let errorMessage = 'An error occurred';

        if (typeof errorData === 'string') {
          try {
            const parsed = JSON.parse(errorData) as { message?: string; error?: string };
            errorMessage = parsed.message || parsed.error || errorData;
          } catch {
            errorMessage = errorData;
          }
        } else if (typeof errorData === 'object' && errorData !== null) {
          const body = errorData as {
            message?: unknown;
            error?: unknown;
            code?: unknown;
            errors?: unknown;
          };
          if (typeof body.message === 'string') {
            errorMessage = body.message;
          } else if (typeof body.error === 'string') {
            errorMessage = body.error;
          } else if (Array.isArray(body.errors)) {
            errorMessage = body.errors.filter((item): item is string => typeof item === 'string').join(', ');
          }
        }

        const errorCode = typeof errorData === 'object' && errorData !== null
          && typeof (errorData as { code?: unknown }).code === 'string'
          ? (errorData as { code: string }).code
          : undefined;
        const retryAfterHeader = error.response.headers?.['retry-after'];
        const retryAfterSeconds = Number.parseInt(String(retryAfterHeader || ''), 10);
        return Promise.reject(new ApiError(
          errorMessage,
          error.response.status,
          error,
          errorCode,
          Number.isFinite(retryAfterSeconds) ? retryAfterSeconds : undefined,
        ));
      }

      return Promise.reject(error);
    }
  );

  return instance;
};
